import { Injectable } from '@angular/core';

import { filter, firstValueFrom, map, Subject } from 'rxjs';

import { Category, PcvizLayer, PcvizLayerType, PcvizProject, Property } from '@mirage-projects/cw-models';

import { StringHelperService } from '../string/string-helper.service';

export interface CreateVectorLayerParams {
    layerName: string;
    geometryType: string;
    categoryId?: number;
    color?: string;
    shouldAutomaticAttrsCheck?: boolean;
    previousCategoryAttrs?: Record<string, Property>;
    wksParentId?: number;
    coordinateSystem?: string;
    description?: string;
    isTargetCat?: boolean;
}

type CreateVectorLayerServiceMsg = {
    request: CreateVectorLayerParams;
    response?: PcvizLayer;
};

/**
 * Service that provides several layer utility functions
 */
@Injectable({ providedIn: 'root' })
export class LayerHelperService {
    private readonly createVectorLayerSubject$: Subject<CreateVectorLayerServiceMsg> = new Subject();

    constructor(private stringHelperService: StringHelperService) {}

    /**
     * Gets a default layer name based on the previous layers
     *
     * @param pcvizProject The project containing the layers
     * @param layerType The type of the layer
     * @param referenceCategory The category of the layer
     * @param baseLabel Base label for the default name
     */
    getDefaultLayerName(pcvizProject: PcvizProject, layerType: PcvizLayerType, referenceCategory: Category, baseLabel: string): string {
        const projectLayers = pcvizProject?.getAllLayers() ?? [];
        const referenceLayers = projectLayers.filter(
            (layer) => layer.getType() === layerType.VECTOR && layer.getCategory() === referenceCategory.name
        );
        const names = referenceLayers.map((layer) => layer.getName());

        return this.stringHelperService.getDefaultName(baseLabel, names, '', false);
    }

    /**
     * By pass method to allow to call to create vector layer method when is not possible to import the CreateVectorLayerService directly.
     * @param createParams Parameters to pass  to the create vector layer function
     */
    createVectorLayer(createParams: CreateVectorLayerParams): Promise<PcvizLayer> {
        const requestMsg = { request: createParams };
        const layerPromise = firstValueFrom(
            this.createVectorLayerSubject$.pipe(
                filter((msg) => !!msg.response),
                map((msg) => msg.response)
            )
        );
        this.createVectorLayerSubject$.next(requestMsg);
        return layerPromise as Promise<PcvizLayer>;
    }

    /**
     * Method to be able to detect createVectorLayer requests from the CreateVectorLayerService
     * @param subscriberFn Function that shoul create the vector layer inside the CreateVectorLayerService context
     */
    subscribeCreateVectorLayerRequests(subscriberFn: (params: CreateVectorLayerParams) => Promise<PcvizLayer>): void {
        this.createVectorLayerSubject$
            .pipe(
                filter((msg) => !msg.response),
                map((msg) => msg.request)
            )
            .subscribe(async (request) => {
                const layer = await subscriberFn(request);
                this.emitNewVectorLayer(layer, request);
            });
    }

    private emitNewVectorLayer(layer: PcvizLayer, request: CreateVectorLayerParams): void {
        const responseMsg = { response: layer, request };
        this.createVectorLayerSubject$.next(responseMsg);
    }
}
